import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './config';
import { getCurrentUserIdToken, getCurrentUserUid } from '../firebase/auth';
import { showRequestNotice } from '../services/requestNotice';

declare module 'axios' {
  interface AxiosRequestConfig {
    // Set on the sign-in and sign-out calls themselves: their 401 must never start the "access changed" handler below.
    skipAuthRetry?: boolean;
    // Set by the handler on the one retry it makes, so a request is never retried twice.
    authRetried?: boolean;
  }
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  let token: string | null;
  try {
    token = await getCurrentUserIdToken();
  } catch (error) {
    // The sign-in and sign-out calls go out even when no token can be had (a refresh that fails on the network): the
    // logout then reaches the gateway by the session cookie, so the server session is still revoked. Every other call
    // fails as before.
    if (!config.skipAuthRetry) throw error;
    token = null;
  }

  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('X-Firebase-ID-Token', token);
    config.headers = headers;
  }

  return config;
});

// "YOUR ACCESS HAS CHANGED" (27 Oct, the sign-in build, step 7). When an admin grants, changes or removes a role, the
// gateway revokes the account's sessions, and its next answer to this browser is a 401 from its sign-in barrier, with
// one of the barrier's codes (BARRIER_REFUSAL_CODES; since 28 Oct, item 1).
// Then, once: force a fresh Firebase token. If Firebase refuses it as over (the refresh token was revoked, the account
// disabled), or the gateway still refuses the fresh one, the session is over: sign out to the sign-in page with "Your
// access has changed". A refresh that fails for any other reason (the network) only fails the request.
// If the fresh token is accepted (the role changed and the new token carries it), the request simply goes through.
//
// IT CANNOT LOOP: each request is retried at most once (authRetried); the sign-in and sign-out calls never enter
// (skipAuthRetry); every 401 arriving at the same time shares one refresh; and the session is ended once per page.
// IT ONLY ACTS ON THE BARRIER'S 401, recognised by its code: any other 401 (for example a route's own "no customer
// record") is about the request, not the session, and is passed back untouched. Nothing happens when nobody is signed
// in.
//
// A SERVER HICCUP NEVER SIGNS ANYONE OUT (28 Oct, item 1). When the gateway could not check the sign-in at all (a
// Firebase outage), it answers 503 AUTH_UNAVAILABLE: the person stays signed in, sees "please try again"
// (services/requestNotice.ts), and the request fails to its caller with no retry. A 429 RATE_LIMITED (item 2) is told
// the same way.
//
// COVERAGE: only calls made through this client (the profile save, the AI-modification API, the session sync and
// customer read in the session bridge, the shared form's request hook). The other gateway calls (search, video detail,
// pricing, collections, trending, the admin screens, checkout, the order pages, uploads) have their own clients and
// move onto this one in PR (c).

// Firebase's answers to a forced refresh that mean the session is over. Anything else (a network failure, say) is not
// proof of that: the original 401 is passed back and nobody is signed out on a blip.
const SESSION_OVER_CODES = ['auth/user-token-expired', 'auth/user-disabled', 'auth/invalid-user-token', 'auth/user-not-found'];

type Refresh = { token: string | null; over: boolean };

let refreshing: Promise<Refresh> | null = null;
let sessionEnded = false;

const refreshTokenOnce = (): Promise<Refresh> => {
  if (!refreshing) {
    refreshing = getCurrentUserIdToken(true)
      .then((token): Refresh => ({ token, over: !token }))
      .catch((error): Refresh => ({ token: null, over: SESSION_OVER_CODES.includes(error?.code) }))
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

// The gateway barrier's codes (CodeWord-Back-NEW src/constants/authFailure.constant.ts). A contract: renaming one is a
// change to both repos.
const BARRIER_REFUSAL_CODES = ['AUTH_REQUIRED', 'AUTH_TOKEN_INVALID', 'AUTH_SESSION_EXPIRED', 'AUTH_SESSION_REVOKED'];
const AUTH_UNAVAILABLE_CODE = 'AUTH_UNAVAILABLE';
const RATE_LIMITED_CODE = 'RATE_LIMITED';

// An error body is small JSON; its code is plain ASCII, so a byte-per-character read finds it where TextDecoder is
// missing (the test runner's jsdom).
const decodeBytes = (buffer: ArrayBuffer): string =>
  typeof TextDecoder !== 'undefined'
    ? new TextDecoder().decode(buffer)
    : Array.from(new Uint8Array(buffer), (byte) => String.fromCharCode(byte)).join('');

// The code in the gateway's error envelope, { error: { code, message } }. A download asked for an ArrayBuffer or a Blob,
// so its error body arrives as bytes and is read as JSON here. Anything unreadable has no code.
const errorCodeOf = async (error: AxiosError): Promise<string | undefined> => {
  let body: unknown = error.response?.data;
  try {
    if (typeof Blob !== 'undefined' && body instanceof Blob) body = await body.text();
    if (body instanceof ArrayBuffer) body = decodeBytes(body);
    if (typeof body === 'string') body = JSON.parse(body);
  } catch {
    return undefined;
  }
  const code = (body as { error?: { code?: unknown } } | null | undefined)?.error?.code;
  return typeof code === 'string' ? code : undefined;
};

const endSession = async (): Promise<void> => {
  if (sessionEnded) return;
  sessionEnded = true;
  // Loaded when needed: services/signOut.ts itself calls this client.
  const { signOut } = await import('../services/signOut');
  await signOut('access-changed');
};

apiClient.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as InternalAxiosRequestConfig | undefined;
  const status = error.response?.status;
  if (status !== 401 && status !== 503 && status !== 429) throw error;

  const code = await errorCodeOf(error);
  if ((status === 503 && code === AUTH_UNAVAILABLE_CODE) || (status === 429 && code === RATE_LIMITED_CODE)) {
    showRequestNotice(status === 503 ? 'auth-unavailable' : 'rate-limited');
    throw error;
  }

  const barrierRefusal = status === 401 && code !== undefined && BARRIER_REFUSAL_CODES.includes(code);
  if (!config || config.skipAuthRetry || !barrierRefusal || !getCurrentUserUid()) {
    throw error;
  }

  if (config.authRetried) {
    // The fresh token was refused too.
    await endSession();
    throw error;
  }

  const { token, over } = await refreshTokenOnce();
  if (!token) {
    if (over) await endSession();
    throw error;
  }

  config.authRetried = true;
  return apiClient.request(config);
});

/** Tests only: forget a shared refresh and an ended session. */
export const __resetAuthRetryForTests = () => {
  refreshing = null;
  sessionEnded = false;
};

export default apiClient;
